import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@material-ui/core';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import { DrawingCanvas } from '../../../../components';

const S = {
  Dialog: styled(Dialog)`
    .MuiPaper-root {
      width: 328px;
      margin: 1rem;
      padding: 8px;
    }
  `,
  DialogTitle: styled(DialogTitle)`
    padding: 24px 16px 16px;
  `,
  Heading: styled.h3`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 1.2;
  `,
  DialogContent: styled(DialogContent)`
    padding: 8px 16px;
    color: rgba(0, 0, 0, 0.54);
    font-size: 16px;
    line-height: 1.5;
  `,
  CanvasWrapper: styled.div`
    overflow: hidden;
    border: solid 1px ${({ theme }) => theme.palette.grey[300]};
    border-radius: 4px;
    background-color: ${({ theme }) => theme.palette.grey[50]};
  `,
  DialogActions: styled(DialogActions)`
    padding: 0 8px;
  `,
  ClearButton: styled(Button)`
    min-width: 0;
    margin-right: auto;
    padding: 10px 8px 9px 8px;
    color: ${({ theme }) => theme.palette.grey[600]};
    line-height: 1.5;
    letter-spacing: 0.5;
  `,
  CancelButton: styled(Button)`
    min-width: 0;
    padding: 8px 8px 7px 8px;
    color: ${({ theme }) => theme.palette.grey[500]};
    line-height: 1.5;
    letter-spacing: 0.5;
  `,
  InputButton: styled(Button)`
    min-width: 0;
    padding: 10px 8px 9px 8px;
    line-height: 1.5;
    letter-spacing: 0.5;
    transition: opacity 0.1s;
    :disabled {
      opacity: 0.38;
      color: ${({ theme }) => theme.palette.secondary.main};
    }
  `,
};

const SignatureDialog = ({ open, onClose, onSign }) => {
  const [dirty, setDirty] = useState(false);
  const [saveData, setSaveData] = useState(null);

  const drawingCanvasRef = useRef(null);
  const dialogRef = useRef(null);

  const clearCanvas = useCallback(() => drawingCanvasRef.current.clear(), []);
  const handleClear = useCallback(() => {
    clearCanvas();

    setDirty(false);
  }, [clearCanvas]);
  const handleSign = useCallback(() => {
    const data = drawingCanvasRef.current.getSaveData();
    const dataURL = drawingCanvasRef.current.toDataURL();

    setSaveData(data);

    onSign(dataURL);

    onClose();
  }, [onClose, onSign]);
  const handleDrawingStart = useCallback(() => setDirty(true), []);
  const handleEntered = useCallback(() => disableBodyScroll(dialogRef.current), []);
  const handleExit = useCallback(() => enableBodyScroll(dialogRef.current), []);
  const handleExited = useCallback(() => setDirty(false), []);

  return (
    <S.Dialog
      open={open}
      onClose={onClose}
      onEntered={handleEntered}
      onExit={handleExit}
      onExited={handleExited}
      ref={dialogRef}
    >
      <S.DialogTitle disableTypography>
        <S.Heading>서명하기</S.Heading>
      </S.DialogTitle>
      <S.DialogContent>
        <DrawingCanvas
          ref={drawingCanvasRef}
          style={{
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            backgroundColor: '#fafafa',
          }}
          loadTimeOffset={0}
          lazyRadius={0}
          brushRadius={2}
          canvasWidth={280}
          canvasHeight={186}
          saveData={saveData}
          onDrawingStart={handleDrawingStart}
          hideGrid
        />
      </S.DialogContent>
      <S.DialogActions>
        {dirty && (
          <S.ClearButton
            type="button"
            onClick={handleClear}
          >
            다시 그리기
          </S.ClearButton>
        )}
        <S.CancelButton
          type="button"
          onClick={onClose}
        >
          취소
        </S.CancelButton>
        <S.InputButton
          type="button"
          color="secondary"
          onClick={handleSign}
          disabled={!dirty}
        >
          완료
        </S.InputButton>
      </S.DialogActions>
    </S.Dialog>
  );
};

SignatureDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSign: PropTypes.func.isRequired,
};

export default SignatureDialog;
